"use client"

import React, { useState, useEffect, useRef, useContext } from "react"
import { Lock, LayoutList, LayoutTemplateIcon as LayoutTable, Smartphone } from "lucide-react"
import { UserSettingsContext } from "../../../context/UserSettingsContext"
import { ChevronsDown } from "lucide-react"
import Loading from "../../Loading"
import AnnualQuarterSwitch from "./AnnualQuarterSwitch"

// Tabsコンポーネント
const Tabs = ({ children, defaultValue, className, onValueChange }) => {
  const [activeTab, setActiveTab] = useState(defaultValue)
  const handleTabChange = (value) => {
    setActiveTab(value)
    if (onValueChange) {
      onValueChange(value)
    }
  }

  return (
    <div className={className}>
      {React.Children.map(children, (child) => {
        if (child.type === TabsList || child.type === TabsContent) {
          return React.cloneElement(child, { activeTab, onTabChange: handleTabChange })
        }
        return child
      })}
    </div>
  )
}

// TabsListコンポーネント
const TabsList = ({ children, activeTab, onTabChange }) => {
  return (
    <div className="flex mb-4 border-b overflow-x-auto">
      {React.Children.map(children, (child) => {
        if (child.type === TabsTrigger) {
          return React.cloneElement(child, { activeTab, onTabChange })
        }
        return child
      })}
    </div>
  )
}

// TabsTriggerコンポーネント
const TabsTrigger = ({ value, children, activeTab, onTabChange }) => {
  return (
    <button
      className={`px-4 py-2 whitespace-nowrap ${
        activeTab === value ? "border-b-2 border-grey-500 text-grey-500" : "text-gray-400"
      }`}
      onClick={() => onTabChange(value)}
    >
      {children}
    </button>
  )
}

// TabsContentコンポーネント
const TabsContent = ({ value, children, activeTab }) => {
  if (value !== activeTab) return null
  return <div>{children}</div>
}

// 数値を日本円表示用にフォーマット（単位: 百万円）
const formatCurrency = (value) => {
  if (value === null || value === undefined) return "-"
  return new Intl.NumberFormat("ja-JP", {
    style: "decimal",
    maximumFractionDigits: 0,
  }).format(value / 1000000)
}

// パーセント表示用にフォーマット
const formatPercent = (value) => {
  if (value === null || value === undefined) return "-"
  return `${value.toFixed(1)}%`
}

const doNothing = (value) => {
  return value
}

const LongTermFinancialResults = ({
  financialData,
  updateSelectedAccounts,
  selectedAccounts,
  selectedPeriod,
  handlePeriodChange,
}) => {
  const [activeTab, setActiveTab] = useState("income")
  const [selectedRows, setSelectedRows] = useState([])
  const scrollContainerRef = useRef(null)
  const { selectableAccounts } = useContext(UserSettingsContext)
  const [open, setOpen] = useState(false)
  const THRESHOLD = 99
  const [isMobile, setIsMobile] = useState(false)
  const [isVerticalLayout, setIsVerticalLayout] = useState(false)

  // 画面サイズの検出
  useEffect(() => {
    const checkIfMobile = () => {
      const mobile = window.innerWidth < 768
      setIsMobile(mobile)
      // スマホサイズの場合は自動的に縦レイアウトに設定
      if (mobile) {
        setIsVerticalLayout(true)
      }
    }

    // 初期チェック
    checkIfMobile()

    // リサイズイベントのリスナー
    window.addEventListener("resize", checkIfMobile)

    // クリーンアップ
    return () => {
      window.removeEventListener("resize", checkIfMobile)
    }
  }, [])

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current
    if (scrollContainer) {
      // コンテナの最大スクロール幅を取得し、右端に設定
      scrollContainer.scrollLeft = scrollContainer.scrollWidth
    }
  }, [financialData, activeTab, isVerticalLayout])

  // 初回レンダリング時に最初の行を選択
  useEffect(() => {
    if (activeTab && selectableAccounts && selectedAccounts) {
      setSelectedRows(selectedAccounts.map((acc) => acc.col)) // 最初の行を選択
    }
  }, [])

  if (!selectableAccounts) {
    return <Loading />
  }

  // 各タブの列情報を定義
  const incomeColumns = selectableAccounts
    .filter((account) => account.category_1.category === "pl")
    .map((account) => ({
      key: account.col,
      label: account.col_name,
      unit: account.unit,
      format: account.col === "eps" ? doNothing : account.unit === "value" ? formatCurrency : formatPercent,
    }))

  const bsColumns = selectableAccounts
    .filter((account) => account.category_1.category === "bs")
    .map((account) => ({
      key: account.col,
      label: account.col_name,
      unit: account.unit,
      format: account.unit === "value" ? formatCurrency : formatPercent,
    }))

  const cashflowColumns = selectableAccounts
    .filter((account) => account.category_1.category === "cf")
    .map((account) => ({
      key: account.col,
      label: account.col_name,
      unit: account.unit,
      format: account.unit === "value" ? formatCurrency : formatPercent,
    }))

  const ratiosColumns = selectableAccounts
    .filter((account) => account.category_1.category === "indicator")
    .map((account) => ({
      key: account.col,
      label: account.col_name,
      unit: account.unit,
      format: account.unit === "value" ? formatCurrency : formatPercent,
    }))

  // 各タブに対応する列を取得する関数
  const getColumnsForTab = (tab) => {
    switch (tab) {
      case "bs":
        return bsColumns
      case "cashflow":
        return cashflowColumns
      case "ratios":
        return ratiosColumns
      default:
        return incomeColumns
    }
  }

  // 行選択時
  const handleRowClick = (column) => {
    setSelectedRows(
      (prev) =>
        prev.includes(column.key)
          ? prev.filter((key) => key !== column.key) // 既に選択されている場合は削除
          : [...prev, column.key], // 選択されていない場合は追加
    )
    updateSelectedAccounts(column.key)
  }

  const toDate = (date) => {
    if (date) {
      const year = date.slice(0, 4)
      const month = parseInt(date.slice(5, 7), 10);
      return `${year}/${month}`
    } else {
      return ""
    }
  }

  // 横表示用のテーブルレンダリング
  const renderHorizontalTable = (data, columns) => (
    <div className={`overflow-x-auto overflow-y-auto w-full ${!open && "h-[280px]"}`} ref={scrollContainerRef}>
      <table className="w-full border-collapse border-none rounded-lg m-0 px-0">
        <thead>
          <tr>
            <th className="sticky top-0 left-0 z-20 w-[80px] min-w-[80px] md:w-[200px] md:min-w-[200px] text-left bg-gray-100">
              項目
            </th>
            {data.map((dt) => (
              <th
                key={dt.fiscal_year + dt.type_of_period}
                className="sticky top-0 text-right bg-gray-100 min-w-[130px] z-10"
              >
                {selectedPeriod === "annual" ? toDate(dt.fiscal_date) : `${toDate(dt.fiscal_date)} (${dt.type_of_period})`}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {columns.map((column) => (
            <tr
              key={column.key}
              onClick={() => handleRowClick(column)}
              className={selectedRows.includes(column.key) ? "bg-[#f3fff3]" : "bg-white hover:bg-[rgb(245,245,245)]"}
            >
              <td className="sticky left-0 z-10 font-medium w-[80px] min-w-[80px] md:w-[200px] md:min-w-[200px] bg-gray-100 cursor-pointer p-3 border-b border-gray-300">
                {column.label}
              </td>
              {data.map((dt, index) => (
                <td
                  key={dt.fiscal_year + dt.type_of_period}
                  className={`text-right min-w-[120px] cursor-pointer p-3 border-b border-gray-300`}
                >
                  {index > data.length - THRESHOLD ? (
                    column.format(dt[column.key])
                  ) : (
                    <Lock className="inline-block w-4 h-4 text-gray-400" />
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )

  // 縦表示用のテーブルレンダリング
  const renderVerticalTable = (data, columns) => {
    // 表示するデータを制限（THRESHOLD以上の最新データのみ）
    const visibleData = data.slice(Math.max(0, data.length - THRESHOLD))

    return (
      <div className={`overflow-x-auto overflow-y-auto w-full ${!open && "h-[280px]"}`} ref={scrollContainerRef}>
        <table className="w-full border-collapse border-none rounded-lg m-0 px-0">
          <thead>
            <tr>
              <th className="sticky top-0 left-0 z-20 w-[80px] min-w-[80px] md:w-[100px] md:min-w-[100px] text-left bg-gray-100">
                期間
              </th>
              {columns.map((column) => (
                <th
                  key={column.key}
                  className={`sticky top-0 text-right bg-gray-100 min-w-[120px] z-10 cursor-pointer ${
                    selectedRows.includes(column.key) ? "bg-[#f3fff3]" : ""
                  }`}
                  onClick={() => handleRowClick(column)}
                >
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visibleData.slice().reverse().map((dt) => (
              <tr key={dt.fiscal_year + dt.type_of_period}>
                <td className="sticky left-0 z-10 font-medium w-[80px] min-w-[80px] md:w-[130px] md:min-w-[130px] bg-gray-100 p-3 border-b border-gray-300">
                {selectedPeriod === "annual" ? toDate(dt.fiscal_date) : `${toDate(dt.fiscal_date)} (${dt.type_of_period})`}
                </td>
                {columns.map((column) => (
                  <td
                    key={column.key}
                    className={`text-right min-w-[120px] p-3 border-b border-gray-300 cursor-pointer ${
                      selectedRows.includes(column.key) ? "bg-[#f3fff3]" : ""
                    }`}
                    onClick={() => handleRowClick(column)}
                  >
                    {column.format(dt[column.key])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  }

  // レイアウト切替ボタン
  const LayoutToggleButton = () => (
    <button
      onClick={() => setIsVerticalLayout(!isVerticalLayout)}
      className="flex items-center gap-1 text-sm px-3 py-1 bg-gray-100 hover:bg-gray-200 rounded-md transition"
      title={isVerticalLayout ? "横表示に切り替え" : "縦表示に切り替え"}
    >
      {isVerticalLayout ? (
        <>
          <LayoutTable className="w-4 h-4" />
          <span className="hidden sm:inline">横表示</span>
        </>
      ) : (
        <>
          <LayoutList className="w-4 h-4" />
          <span className="hidden sm:inline">縦表示</span>
        </>
      )}
    </button>
  )

  return (
    <div className="dashboard-row" style={{ marginTop: 0, paddingBottom: 5 }}>
      <div className="flex flex-wrap items-center gap-4 mb-2">
        <div>
          <h3>業績推移</h3>
        </div>
        <div>
          <AnnualQuarterSwitch onPeriodChange={handlePeriodChange} height="28px" period={selectedPeriod} />
        </div>
        <div className="ml-auto flex items-center gap-2">
          {isMobile && (
            <div className="text-xs text-gray-500 flex items-center">
              <Smartphone className="w-3 h-3 mr-1" />
              <span>モバイル表示</span>
            </div>
          )}
          <LayoutToggleButton />
        </div>
      </div>
      <div className="forecast-container py-1">
        <Tabs defaultValue="income" className="w-full" onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="income">損益計算書</TabsTrigger>
            <TabsTrigger value="bs">貸借対照表</TabsTrigger>
            <TabsTrigger value="cashflow">キャッシュフロー計算書</TabsTrigger>
            <TabsTrigger value="ratios">財務指標</TabsTrigger>
          </TabsList>

          <TabsContent value="income">
            {isVerticalLayout
              ? renderVerticalTable(financialData, incomeColumns)
              : renderHorizontalTable(financialData, incomeColumns)}
          </TabsContent>

          <TabsContent value="bs">
            {isVerticalLayout
              ? renderVerticalTable(financialData, bsColumns)
              : renderHorizontalTable(financialData, bsColumns)}
          </TabsContent>

          <TabsContent value="cashflow">
            {isVerticalLayout
              ? renderVerticalTable(financialData, cashflowColumns)
              : renderHorizontalTable(financialData, cashflowColumns)}
          </TabsContent>

          <TabsContent value="ratios">
            {isVerticalLayout
              ? renderVerticalTable(financialData, ratiosColumns)
              : renderHorizontalTable(financialData, ratiosColumns)}
          </TabsContent>
        </Tabs>
      </div>
      <div className="flex justify-center items-center">
        <ChevronsDown
          className={`
            ${open ? "rotate-180" : ""}
            text-gray-500 hover:text-gray-600
            hover:bg-gray-200
            rounded-full
            p-1 transition duration-200
            cursor-pointer
          `}
          onClick={() => setOpen(!open)}
        />
      </div>
    </div>
  )
}

export default LongTermFinancialResults
